import { computed, watch } from "vue";
import { useRouter } from "vue-router";

import { useBreadcrumbs } from "@/components/TBreadcrumbs/useBreadcrumbs";
import { useFlag } from "@/composables/useFlags/useFlag";
import {
  TITAN_REMOTE_CRUMBS_CHANGE_EVENT,
  isEmbedded,
} from "@/remote/contract";
import { isShellOwnedRoute } from "@/router/shellOwned";
import { useAuthenticationStore } from "@/store/authentication.store";
import { isOrgTier, orgLanding, orgTierOf } from "@/utils/navigation/orgTier";

/**
 * The label the host gives the root of Titan's trail when embedded: in the shell the user is
 * inside the Cybersecurity product, not at Titan's org root. Hard-coded because `useBreadcrumbs`
 * builds the rest of the trail from untranslated strings too.
 */
export const EMBEDDED_ROOT_CRUMB_LABEL = "Cybersecurity";

/** One crumb as the host consumes it. Matches what red-app publishes. */
type PublishedCrumb = {
  id: string;
  label: string;
  href?: string;
};

/** The org an org-scoped crumb url stands for, or null for any other url. */
const crumbOrgUuid = (url: string): string | null => {
  const [segment, uuid] = url.split("/").filter(Boolean);
  return isOrgTier(segment) && uuid ? uuid : null;
};

/**
 * Publishes Titan's breadcrumb trail to the host as DATA — the host draws it, in one component,
 * for every app it embeds. Standalone this is inert: `TUpperNav` renders `TBreadcrumbs` itself,
 * and the layouts already hide that whole header when embedded.
 */
export function useCrumbsPublisher(host: HTMLElement | null): void {
  // Also stops `useBreadcrumbs` running twice standalone, where TBreadcrumbs owns it.
  if (!isEmbedded() || !host) return;

  const router = useRouter();
  const authStore = useAuthenticationStore();
  const { isFeatureAvailable: isDashboardAvailableCustomer } = useFlag(
    "DASHBOARD_OVERVIEW_CUSTOMER",
  );

  // `items`, not `visibleItems`: the host decides what to collapse, so it needs the full trail.
  const { items } = useBreadcrumbs(isDashboardAvailableCustomer.value);

  // `resolve().href` applies the router base the host passed to `setRouterBase`, so the href is
  // host-absolute (`/thq/customer/…`) and its router can navigate it unchanged. A shell-owned
  // route gets no href: embedded, the guard bounces it to `/` and the user lands on sign-in's
  // default company instead of where the crumb said.
  const hrefOf = (url: string): { href: string } | Record<never, never> => {
    if (!url) return {};
    const resolved = router.resolve(url);
    return isShellOwnedRoute(resolved) ? {} : { href: resolved.href };
  };

  /** The signed-in account's own landing, or null when the store cannot say. */
  const accountRoot = (): { uuid: string; url: string } | null => {
    const account = authStore.selectedAccount;
    const tier = orgTierOf(account?.type_name);
    if (!account?.uuid || !tier) return null;
    return { uuid: account.uuid, url: orgLanding(tier, account.uuid) };
  };

  const crumbs = computed<PublishedCrumb[]>(() => {
    const trail = items.value;
    if (!trail.length) return [];

    const published = trail.map((crumb, index) => ({
      // Titan's crumbs carry no id, so one is derived. Index-prefixed because a trail can
      // legitimately repeat a name, and the host uses this as a track-by key.
      id: `${index}-${crumb.url || crumb.name}`,
      label: crumb.name,
      ...hrefOf(crumb.url),
    }));

    // A trail that does not start at the signed-in account (an MSP's customer, seen by a System
    // user) gets the account's root prepended, so the crumb the host labels with the root
    // company's name navigates there and not to the MSP. Otherwise the first crumb IS that root,
    // and only its label changes.
    const root = accountRoot();
    if (root && crumbOrgUuid(trail[0].url) !== root.uuid) {
      return [
        {
          id: `root-${root.uuid}`,
          label: EMBEDDED_ROOT_CRUMB_LABEL,
          ...hrefOf(root.url),
        },
        ...published,
      ];
    }
    published[0].label = EMBEDDED_ROOT_CRUMB_LABEL;
    return published;
  });

  watch(
    crumbs,
    (detail) => {
      // Bubbles + composed so the host can listen on `document` rather than on this element,
      // which it mounts lazily. Fires with an empty trail too — that is how a header clears.
      host.dispatchEvent(
        new CustomEvent<PublishedCrumb[]>(TITAN_REMOTE_CRUMBS_CHANGE_EVENT, {
          detail,
          bubbles: true,
          composed: true,
        }),
      );
    },
    { immediate: true },
  );
}

export default useCrumbsPublisher;
